import { useForm } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import type { FilterStore } from 'components/Filters/FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'components/UI/BaseDropdown/dropdown-base'
import { DatePicker } from 'components/FormUtils/DatePicker'
import FbButton from 'components/FbUI/FbButton'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'
import { cn } from 'components/UI/cn'
import {
  FILTER_POPOVER_BODY,
  FILTER_POPOVER_FOOTER,
  FILTER_POPOVER_MOBILE_CAP,
} from './popoverClasses'

interface DatePreset {
  label: string
  value: string
  getDates: () => { min: string; max: string }
}

const getDatePresets = (): DatePreset[] => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)

  const formatDate = (date: Date) => date.toISOString().split('T')[0]

  return [
    {
      label: 'Last Week',
      value: 'last_week',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setDate(start.getDate() - 7)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last Month',
      value: 'last_month',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setMonth(start.getMonth() - 1)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last 3 Months',
      value: 'last_3_months',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setMonth(start.getMonth() - 3)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last 6 Months',
      value: 'last_6_months',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setMonth(start.getMonth() - 6)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last Year',
      value: 'last_year',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setFullYear(start.getFullYear() - 1)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
  ]
}

export function DateRangeFilterWithPresets({
  title,
  filterKey,
  filterStore,
  showPresets = true,
}: {
  title: string
  filterKey: string
  filterStore: FilterStore
  showPresets?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const {
    pendingRangeFilters,
    setPendingRangeFilters,
    clearPendingRangeFilter,
    registerFilterTitle,
  } = filterStore()
  const current = pendingRangeFilters?.[filterKey!]

  const currentMin = current?.min != null ? String(current.min) : null
  const currentMax = current?.max != null ? String(current.max) : null

  const { watch, reset, setValue, handleSubmit } = useForm<{
    min: string | null
    max: string | null
  }>({
    defaultValues: {
      min: currentMin,
      max: currentMax,
    },
  })

  const minValue = watch('min')
  const maxValue = watch('max')

  // Show a small inline error when the user has entered both dates and the
  // end is before the start. Doesn't block clicking Apply visually, but the
  // submit handler also short-circuits below so an invalid range never lands.
  const rangeError = useMemo(() => {
    if (!minValue || !maxValue) return false
    return new Date(minValue) > new Date(maxValue)
  }, [minValue, maxValue])

  function saveFilters() {
    if (rangeError) return
    if (minValue !== null || maxValue !== null) {
      setPendingRangeFilters(
        filterKey,
        (minValue as any) || undefined,
        (maxValue as any) || undefined
      )
    }
    setOpen(false)
  }

  const clearFilters = () => {
    reset({
      min: null,
      max: null,
    })
    setSelectedPreset(null)
    clearPendingRangeFilter(filterKey)
  }

  const handlePresetClick = (preset: DatePreset) => {
    const dates = preset.getDates()
    setValue('min', dates.min)
    setValue('max', dates.max)
    setSelectedPreset(preset.value)
  }

  const _handleSubmit = handleSubmit(() => {
    saveFilters()
  })

  const isRangeSet = current?.min != null || current?.max != null

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  // Reflect a matching preset's selection visually when current values match.
  useEffect(() => {
    if (!isRangeSet) {
      setSelectedPreset(null)
    } else if (showPresets && currentMin && currentMax) {
      const matchingPreset = getDatePresets().find((preset) => {
        const dates = preset.getDates()
        return dates.min === currentMin && dates.max === currentMax
      })
      if (matchingPreset) {
        setSelectedPreset(matchingPreset.value)
      }
    }
  }, [isRangeSet, currentMin, currentMax, showPresets])

  // Clear preset selection when dates are manually changed away from a preset.
  useEffect(() => {
    const subscription = watch((value) => {
      const matchingPreset = getDatePresets().find((preset) => {
        const dates = preset.getDates()
        return dates.min === value.min && dates.max === value.max
      })
      if (matchingPreset) {
        setSelectedPreset(matchingPreset.value)
      } else if (!matchingPreset && selectedPreset) {
        setSelectedPreset(null)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  // Compact label on the trigger pill — prefers the matching preset's name
  // (e.g. "Last Week"), otherwise formats the date range, falling back to
  // the field title when nothing is set.
  const formatShort = (raw: string) => {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return raw
    const sameYear = d.getFullYear() === new Date().getFullYear()
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: sameYear ? undefined : '2-digit',
    })
  }
  const triggerLabel = (() => {
    if (!isRangeSet) return title
    if (showPresets && currentMin && currentMax) {
      const matching = getDatePresets().find((p) => {
        const dates = p.getDates()
        return dates.min === currentMin && dates.max === currentMax
      })
      if (matching) return `${title}: ${matching.label}`
    }
    if (currentMin && !currentMax)
      return `${title}: ≥ ${formatShort(currentMin)}`
    if (!currentMin && currentMax)
      return `${title}: ≤ ${formatShort(currentMax)}`
    return `${title}: ${formatShort(currentMin!)} – ${formatShort(currentMax!)}`
  })()

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          // Rebuild the form from the committed store values on each open
          // so a previously-cancelled edit doesn't linger. Mirrors the
          // SelectFilter / DateRangeFilter fix.
          reset({ min: currentMin, max: currentMax })
          setSelectedPreset(null)
        }
        setOpen(nextOpen)
      }}
    >
      <div className={'relative'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            isActive={isRangeSet}
            title={triggerLabel}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isRangeSet}
          open={open}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent
        // Mobile cap (FIR-7449): see popoverClasses.ts. On narrow screens the
        // body stacks vertically (`flex-col sm:flex-row`), making the popover
        // taller; the cap + scrollable body keep the pinned footer reachable.
        className={cn(
          '!rounded-xl !border-neutral-200 !shadow-lg !bg-white !p-0 overflow-hidden',
          FILTER_POPOVER_MOBILE_CAP
        )}
        collisionPadding={12}
        sideOffset={6}
      >
        <div className={cn(FILTER_POPOVER_BODY, 'flex flex-col sm:flex-row')}>
          {/* Left: custom range inputs */}
          <div className="w-full sm:w-[260px] p-3 flex flex-col gap-3">
            <DatePicker
              label="Start Date"
              value={minValue ?? undefined}
              onChange={(v) => setValue('min', v ?? null)}
              allowTyping
            />
            <DatePicker
              label="End Date"
              value={maxValue ?? undefined}
              onChange={(v) => setValue('max', v ?? null)}
              allowTyping
            />
            {rangeError && (
              <p className="m-0 text-xs leading-4 text-error-strong">
                End date must be ≥ start date
              </p>
            )}
          </div>

          {/* Right: quick-select list. Full-width below the inputs on mobile
              (top border), beside them on >=sm (left border). */}
          {showPresets && (
            <div className="w-full sm:w-[180px] border-t sm:border-t-0 sm:border-l border-neutral-200 p-1.5 flex flex-col">
              <div className="text-xs font-semibold text-neutral-500 px-2 pt-1.5 pb-1">
                Quick Select
              </div>
              {getDatePresets().map((preset) => {
                const active = selectedPreset === preset.value
                return (
                  <button
                    type="button"
                    key={preset.value}
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      'w-full text-left px-2 py-2 text-sm rounded-lg transition-colors',
                      'hover:bg-neutral-100',
                      active
                        ? 'bg-neutral-100 text-neutral-900 font-semibold'
                        : 'text-neutral-700'
                    )}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer: pinned below the scrollable body so Apply stays reachable. */}
        <div
          className={cn(
            FILTER_POPOVER_FOOTER,
            'items-center justify-between gap-2 px-3 py-3 border-t border-neutral-200'
          )}
        >
          <button
            type="button"
            onClick={() => {
              reset({ min: null, max: null })
              setSelectedPreset(null)
            }}
            disabled={!minValue && !maxValue}
            className={cn(
              'text-sm font-medium transition-colors',
              !minValue && !maxValue
                ? 'text-neutral-400 cursor-not-allowed'
                : 'text-brand-active hover:text-brand-bright'
            )}
          >
            Reset
          </button>
          <div className="flex gap-2">
            <FbButton
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </FbButton>
            <FbButton
              size="sm"
              type="button"
              className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
              onClick={_handleSubmit}
              // Stays enabled when a filter is currently applied (`isRangeSet`)
              // so Reset doesn't trap the user — they can clear the inputs
              // and Apply the empty state to drop the filter.
              disabled={rangeError || (!isRangeSet && !minValue && !maxValue)}
            >
              Apply
            </FbButton>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
